/**
 * AdminPage Component
 * User and agency administration for the Oath app
 * Uses OathUI components and matches Dashboard design language
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  UsersIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  BanknotesIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  InformationCircleIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  DevicePhoneMobileIcon,
  GlobeAmericasIcon,
  CheckIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import PaymentAccountsSection from './PaymentAccountsSection';
import ExecutingAgentsSection from './ExecutingAgentsSection';
import AdminTabNavigation from './AdminTabNavigation';
import { getAgencyUsers, getAgenciesForManagement, updateAgencyProfile, getExecutingAgents } from '../../../services/oathUserService';
import { toast } from 'react-toastify';
import { getAllStates } from '../../../services/oathLocationService';
import { formatPhoneNumber, stripPhoneFormatting } from './adminUtils';
import { useOathAuth } from '../../../services/oathAuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathButton,
} from '../../shared/OathUI';

// ============================================
// Summary Stat Pill
// ============================================
const StatPill = ({ label, value, variant = 'default', selected = false, onClick }) => {
  const variants = {
    default: selected
      ? 'bg-gray-300 dark:bg-gray-500 text-gray-900 dark:text-white ring-2 ring-gray-400 dark:ring-gray-400'
      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    active: selected
      ? 'bg-green-200 dark:bg-green-800/50 text-green-800 dark:text-green-200 ring-2 ring-green-400 dark:ring-green-500'
      : 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    inactive: selected
      ? 'bg-gray-300 dark:bg-gray-500 text-gray-900 dark:text-white ring-2 ring-gray-400 dark:ring-gray-400'
      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
    indigo: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300',
  };

  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${onClick ? 'cursor-pointer' : ''} ${variants[variant]}`}
    >
      <span>{label}</span>
      <span className="font-bold">{value}</span>
    </Tag>
  );
};

// ============================================
// User avatar with initials
// ============================================
const UserAvatar = ({ firstName, lastName, isActive }) => {
  const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();

  return (
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
      isActive
        ? 'bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light'
        : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
    }`}>
      {initials || '?'}
    </div>
  );
};

// ============================================
// Search input
// ============================================
const SearchInput = ({ placeholder, value, onChange }) => (
  <div className="relative">
    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
    />
  </div>
);

// ============================================
// Loading Skeletons
// ============================================
const AgencyGridSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4">
          <div className="flex items-center gap-3">
            <ShimmerBar className="w-9 h-9 rounded-lg flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <ShimmerBar className="h-4 w-3/4" />
              <ShimmerBar className="h-3 w-full" />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const ShimmerBar = ({ className }) => (
  <div className={`rounded bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 bg-[length:200%_100%] animate-shimmer ${className}`} />
);

const UserGridSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="h-0.5 bg-gray-200 dark:bg-gray-700" />
        <div className="p-4">
          <div className="flex items-center gap-3">
            <ShimmerBar className="w-9 h-9 rounded-lg flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <ShimmerBar className="h-4 w-3/4" />
              <ShimmerBar className="h-3 w-full" />
              <ShimmerBar className="h-3 w-1/2" />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ============================================
// Error State
// ============================================
const ErrorState = ({ message, onRetry }) => (
  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
    <ExclamationTriangleIcon className="h-10 w-10 text-red-400 dark:text-red-500 mx-auto mb-3" />
    <h3 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">Failed to Load</h3>
    <p className="text-sm text-red-600 dark:text-red-400 mb-4">{message}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
      >
        <ArrowPathIcon className="h-4 w-4" />
        Try Again
      </button>
    )}
  </div>
);

// ============================================
// Empty State
// ============================================
const EmptyState = ({ icon: Icon = UsersIcon, title, description, action }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-10 text-center">
    <div className="w-14 h-14 bg-primary/10 dark:bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
      <Icon className="h-7 w-7 text-primary dark:text-primary-light" />
    </div>
    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">{title}</h3>
    <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 max-w-sm mx-auto">{description}</p>
    {action}
  </div>
);

// ============================================
// Agency Card
// ============================================
const AgencyCard = ({ agency, onClick }) => {
  const isActive = agency.status?.toLowerCase() === 'active' || agency.status === 'A';
  const agencyType = agency.agencyType || 'Agency';

  const typeBadgeClass = agencyType === 'General Agency'
    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
    : agencyType === 'Sub-Agency'
      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';

  return (
    <button
      onClick={onClick}
      className={`w-full text-left bg-white dark:bg-gray-800 rounded-xl shadow-md border transition-all duration-150 group overflow-hidden ${
        isActive
          ? 'border-l-2 border-l-primary border-t-gray-200 border-r-gray-200 border-b-gray-200 dark:border-t-gray-700 dark:border-r-gray-700 dark:border-b-gray-700 hover:shadow-lg hover:-translate-y-0.5'
          : 'border-l-2 border-l-gray-300 dark:border-l-gray-600 border-t-gray-200 border-r-gray-200 border-b-gray-200 dark:border-t-gray-700 dark:border-r-gray-700 dark:border-b-gray-700 opacity-70 hover:opacity-100'
      }`}
    >
      {/* Top badge bar */}
      <div className="flex items-center justify-between px-4 pt-3 pb-1.5">
        <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${typeBadgeClass}`}>
          {agencyType}
        </span>
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide ${
          isActive ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-green-500' : 'bg-gray-400 dark:bg-gray-500'}`} />
          {agency.status || (isActive ? 'Active' : 'Inactive')}
        </span>
      </div>

      {/* Main content */}
      <div className="flex items-center gap-3 px-4 pb-3">
        <div className="w-9 h-9 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0">
          <BuildingOfficeIcon className="h-4.5 w-4.5 text-primary dark:text-primary-light" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">
            {agency.dbaAgencyName || agency.legalAgencyName}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            #{agency.agencyId} · {agency.userCount} {agency.userCount === 1 ? 'user' : 'users'} · {agency.paymentAccountCount ?? 0} {(agency.paymentAccountCount ?? 0) === 1 ? 'account' : 'accounts'}
          </p>
        </div>
        <ChevronRightIcon className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-primary dark:group-hover:text-primary-light group-hover:translate-x-0.5 transition-all duration-150 flex-shrink-0" />
      </div>
    </button>
  );
};

// ============================================
// User Card
// ============================================
const UserCard = ({ user, onEdit, isSelf }) => (
  <button
    onClick={() => onEdit(user)}
    className={`w-full text-left bg-white dark:bg-gray-800 rounded-xl shadow-md border transition-all duration-150 overflow-hidden group ${
      user.isActive
        ? 'border-l-2 border-l-primary border-t-gray-200 border-r-gray-200 border-b-gray-200 dark:border-t-gray-700 dark:border-r-gray-700 dark:border-b-gray-700 hover:shadow-lg hover:-translate-y-0.5'
        : 'border-l-2 border-l-gray-300 dark:border-l-gray-600 border-t-gray-200 border-r-gray-200 border-b-gray-200 dark:border-t-gray-700 dark:border-r-gray-700 dark:border-b-gray-700 opacity-70 hover:opacity-100'
    }`}
  >
    <div className="p-4">
      <div className="flex items-start gap-3">
        <UserAvatar
          firstName={user.firstName}
          lastName={user.lastName}
          isActive={user.isActive}
        />
        <div className="flex-1 min-w-0">
          {/* Row 1: Name + badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">
              {user.firstName} {user.lastName}
            </p>
            <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded ${
              user.isActive
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
            }`}>
              {user.isActive ? 'Active' : 'Inactive'}
            </span>
            {isSelf && (
              <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">You</span>
            )}
          </div>
          {/* Row 2: Username */}
          {user.username && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5 font-mono">{user.username}</p>
          )}
          {/* Row 3: Email */}
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
          {/* Row 4: Producer + phone badges */}
          {(user.producerName || user.phoneNumber) && (
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {user.producerName && (
                <>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light">
                    <IdentificationIcon className="h-3 w-3" />
                    {user.producerName}
                  </span>
                  {user.isPrimaryProducer && (
                    <span className="inline-flex px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 uppercase">
                      Primary
                    </span>
                  )}
                </>
              )}
              {user.phoneNumber && (
                <span className="inline-flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500">
                  <PhoneIcon className="h-3 w-3" />
                  {formatPhone(user.phoneNumber)}
                </span>
              )}
            </div>
          )}
        </div>
        {!isSelf && (
          <PencilSquareIcon className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-primary dark:group-hover:text-primary-light transition-all duration-150 flex-shrink-0 mt-0.5" />
        )}
      </div>
    </div>
  </button>
);

// ============================================
// Agency Selection View
// ============================================
const AgencySelectionView = ({ agencies, loading, error, onRetry, onSelectAgency, search, setSearch }) => {
  const [activeOpen, setActiveOpen] = useState(true);
  const [inactiveOpen, setInactiveOpen] = useState(false);
  const filteredAgencies = agencies.filter((agency) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      agency.dbaAgencyName?.toLowerCase().includes(searchLower) ||
      agency.legalAgencyName?.toLowerCase().includes(searchLower) ||
      String(agency.agencyId).includes(searchLower)
    );
  });

  const agencyTypeOrder = { 'General Agency': 0, 'Agency': 1, 'Sub-Agency': 2 };
  const sortedAgencies = [...filteredAgencies].sort((a, b) => {
    const typeA = agencyTypeOrder[a.agencyType] ?? 1;
    const typeB = agencyTypeOrder[b.agencyType] ?? 1;
    if (typeA !== typeB) return typeA - typeB;
    const nameA = (a.dbaAgencyName || a.legalAgencyName || '').toLowerCase();
    const nameB = (b.dbaAgencyName || b.legalAgencyName || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });
  const activeAgencies = sortedAgencies.filter((a) => a.status?.toLowerCase() === 'active' || a.status === 'A');
  const inactiveAgencies = sortedAgencies.filter((a) => a.status && a.status?.toLowerCase() !== 'active' && a.status !== 'A');

  if (loading) {
    return <AgencyGridSkeleton />;
  }

  if (error) {
    return <ErrorState message={error} onRetry={onRetry} />;
  }

  if (agencies.length === 0) {
    return (
      <EmptyState
        icon={BuildingOfficeIcon}
        title="No agencies found"
        description="You don't have access to any agencies for user management"
      />
    );
  }

  return (
    <div className="space-y-4">
      {agencies.length > 1 && (
        <div className="max-w-md">
          <SearchInput
            placeholder="Search agencies..."
            value={search}
            onChange={setSearch}
          />
        </div>
      )}

      {activeAgencies.length > 0 && (
        <div>
          <button
            onClick={() => setActiveOpen(!activeOpen)}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors group ${
              activeOpen
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 mb-3'
                : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700 mb-1'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${activeOpen ? 'bg-green-500' : 'bg-green-400 dark:bg-green-600'}`} />
              <span className={`text-sm font-medium ${activeOpen ? 'text-green-800 dark:text-green-300' : 'text-gray-600 dark:text-gray-400'}`}>
                Active
              </span>
              <span className={`text-xs ${activeOpen ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-500'}`}>({activeAgencies.length})</span>
            </div>
            <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${activeOpen ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400 -rotate-90'}`} />
          </button>
          {activeOpen && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-expand-down">
              {activeAgencies.map((agency, i) => (
                <div key={agency.agencyId} className="animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                  <AgencyCard agency={agency} onClick={() => onSelectAgency(agency)} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {inactiveAgencies.length > 0 && (
        <div>
          <button
            onClick={() => setInactiveOpen(!inactiveOpen)}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors group ${
              inactiveOpen
                ? 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 mb-3'
                : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700 mb-1'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Inactive
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-500">({inactiveAgencies.length})</span>
            </div>
            <ChevronDownIcon className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${inactiveOpen ? '' : '-rotate-90'}`} />
          </button>
          {inactiveOpen && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-expand-down">
              {inactiveAgencies.map((agency, i) => (
                <div key={agency.agencyId} className="animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                  <AgencyCard agency={agency} onClick={() => onSelectAgency(agency)} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {filteredAgencies.length === 0 && search && (
        <EmptyState
          icon={BuildingOfficeIcon}
          title="No matching agencies"
          description="Try adjusting your search term"
        />
      )}
    </div>
  );
};

// ============================================
// Users List View
// ============================================
const UsersListView = ({ agency, currentUserId, canCreateUsers = false, canEditUsers = false }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async () => {
    if (!agency?.agencyId) return;

    setLoading(true);
    setError(null);

    const result = await getAgencyUsers(agency.agencyId);

    if (result.success) {
      setUsers(result.users || []);
    } else {
      const errorLower = (result.error || '').toLowerCase();
      if (errorLower.includes('no users') ||
          errorLower.includes('not found') ||
          errorLower.includes('404') ||
          errorLower.includes('failed to fetch users') ||
          errorLower.includes('empty')) {
        setUsers([]);
      } else {
        setError(result.error);
      }
    }

    setLoading(false);
  }, [agency?.agencyId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEditUser = (user) => {
    navigate(`/app/admin/users/${user.portalUserId}?agencyId=${agency.agencyId}`);
  };

  const handleAddUser = () => {
    navigate(`/app/admin/users/new?agencyId=${agency.agencyId}`, {
      state: {
        agencyName: `${agency.dbaAgencyName || agency.legalAgencyName} (${agency.agencyId})`
      }
    });
  };

  // Derived stats
  const stats = useMemo(() => {
    const active = users.filter((u) => u.isActive).length;
    const inactive = users.length - active;
    return { total: users.length, active, inactive };
  }, [users]);

  // Filter users by search and active status
  const filteredUsers = useMemo(() => {
    let filtered = users;
    if (activeFilter !== null) {
      filtered = filtered.filter((u) => u.isActive === activeFilter);
    }
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.firstName?.toLowerCase().includes(searchLower) ||
          user.lastName?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.username?.toLowerCase().includes(searchLower)
      );
    }
    return filtered;
  }, [users, search, activeFilter]);

  return (
    <div className="space-y-5">
      {/* Stats Row + Search + Add */}
      {!loading && users.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <StatPill label="Total" value={stats.total} selected={activeFilter === null} onClick={() => setActiveFilter(null)} />
            <StatPill label="Active" value={stats.active} variant="active" selected={activeFilter === true} onClick={() => setActiveFilter((f) => f === true ? null : true)} />
            {stats.inactive > 0 && (
              <StatPill label="Inactive" value={stats.inactive} variant="inactive" selected={activeFilter === false} onClick={() => setActiveFilter((f) => f === false ? null : false)} />
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative max-w-xs w-full sm:w-auto">
              <SearchInput
                placeholder="Search users..."
                value={search}
                onChange={setSearch}
              />
            </div>
            {canCreateUsers && (
              <OathButton onClick={handleAddUser} size="sm">
                <UserPlusIcon className="h-4 w-4" />
                Add User
              </OathButton>
            )}
          </div>
        </div>
      )}
      {!loading && users.length === 0 && canCreateUsers && (
        <div className="flex justify-end">
          <OathButton onClick={handleAddUser} size="sm">
            <UserPlusIcon className="h-4 w-4" />
            Add User
          </OathButton>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <UserGridSkeleton />
      ) : error ? (
        <ErrorState message={error} onRetry={fetchUsers} />
      ) : filteredUsers.length === 0 ? (
        <EmptyState
          title={search ? 'No matching users' : 'No users yet'}
          description={
            search
              ? 'Try adjusting your search term'
              : 'Add your first user to get started with this agency.'
          }
          action={
            !search && canCreateUsers ? (
              <OathButton onClick={handleAddUser}>
                <UserPlusIcon className="h-4 w-4" />
                Add User
              </OathButton>
            ) : null
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.map((user) => (
            <UserCard
              key={user.portalUserId}
              user={user}
              onEdit={handleEditUser}
              isSelf={user.portalUserId === currentUserId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// Agency Details View
// ============================================
const formatPhone = (value) => {
  if (!value) return null;
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return value;
};

const formatAddressLines = (address) => {
  if (!address) return null;
  const street = [address.street1, address.street2].filter(Boolean).join(', ');
  const cityStateZip = [[address.city, address.state].filter(Boolean).join(', '), address.zipCode].filter(Boolean).join(' ');
  return [street, cityStateZip].filter(Boolean);
};

const AddressBlock = ({ label, address }) => {
  const lines = formatAddressLines(address);
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">{label}</p>
      <div className="flex items-start gap-1.5 mt-0.5">
        <MapPinIcon className="h-3.5 w-3.5 text-rose-400 dark:text-rose-400 flex-shrink-0 mt-0.5" />
        {lines ? (
          <div className="text-sm text-gray-900 dark:text-white leading-snug">
            {lines.map((line, i) => <p key={i}>{line}</p>)}
          </div>
        ) : (
          <p className="text-sm text-gray-400 dark:text-gray-500">-</p>
        )}
      </div>
    </div>
  );
};

const emptyAddress = { street1: '', street2: '', city: '', state: '', zipCode: '' };

const inputClass = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all';
const selectClass = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all appearance-none';

const StateTypeahead = ({ value, onChange, states = [] }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const ref = useRef(null);
  const listRef = useRef(null);

  const filtered = query
    ? states.filter((s) =>
        s.stateCd.toLowerCase().startsWith(query.toLowerCase()) ||
        s.stateName.toLowerCase().startsWith(query.toLowerCase())
      )
    : states;

  const displayValue = value || '';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (query) {
      setHighlightIndex(0);
    } else {
      // When opening without a query, highlight the currently selected value
      const idx = filtered.findIndex((s) => s.stateCd === value);
      setHighlightIndex(idx >= 0 ? idx : 0);
    }
  }, [query, filtered, value]);

  const inputRef = useRef(null);

  const handleSelect = (stateCd) => {
    onChange(stateCd);
    setQuery('');
    setOpen(false);
    // Move focus to next focusable input (zip code)
    const container = ref.current?.closest('.grid');
    if (container) {
      const inputs = [...container.querySelectorAll('input')];
      const currentIdx = inputs.indexOf(inputRef.current);
      if (currentIdx >= 0 && inputs[currentIdx + 1]) {
        inputs[currentIdx + 1].focus();
      }
    }
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0) handleSelect(filtered[highlightIndex].stateCd);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <input
        ref={inputRef}
        type="text"
        value={open ? query : displayValue}
        placeholder="State"
        autoComplete="off"
        data-1p-ignore
        data-lpignore="true"
        className={inputClass}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={handleKeyDown}
      />
      <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
      {open && (
        <div ref={listRef} className="absolute z-20 bottom-full mb-1 w-full max-h-40 overflow-y-auto bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg">
          {filtered.length === 0 ? (
            <p className="px-2.5 py-1.5 text-xs text-gray-400">No matches</p>
          ) : (
            filtered.map((s, i) => (
              <button
                key={s.stateCd}
                type="button"
                ref={i === highlightIndex ? (el) => { if (el && !query) el.scrollIntoView({ block: 'nearest' }); } : undefined}
                onClick={() => handleSelect(s.stateCd)}
                className={`w-full text-left px-2.5 py-1.5 text-sm transition-colors ${
                  i === highlightIndex
                    ? 'bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light'
                    : s.stateCd === value
                      ? 'bg-primary/5 dark:bg-primary/10 text-primary dark:text-primary-light font-medium'
                      : 'text-gray-900 dark:text-white hover:bg-primary/10 dark:hover:bg-primary/20'
                }`}
              >
                {s.stateCd} — {s.stateName}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const AddressForm = ({ label, address, onChange, states = [], actionButton }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">{label}</p>
      {actionButton}
    </div>
    <div className="space-y-1.5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        <input type="text" placeholder="Street 1" autoComplete="off" data-1p-ignore data-lpignore="true" value={address.street1 || ''} onChange={(e) => onChange({ ...address, street1: e.target.value })} className={inputClass} />
        <input type="text" placeholder="Street 2 (optional)" autoComplete="off" data-1p-ignore data-lpignore="true" value={address.street2 || ''} onChange={(e) => onChange({ ...address, street2: e.target.value })} className={inputClass} />
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        <input type="text" placeholder="City" autoComplete="off" data-1p-ignore data-lpignore="true" value={address.city || ''} onChange={(e) => onChange({ ...address, city: e.target.value })} className={inputClass} />
        <StateTypeahead value={address.state} onChange={(stateCd) => onChange({ ...address, state: stateCd })} states={states} />
        <input type="text" placeholder="Zip" autoComplete="off" data-1p-ignore data-lpignore="true" value={address.zipCode || ''} onChange={(e) => onChange({ ...address, zipCode: e.target.value })} className={inputClass} />
      </div>
    </div>
  </div>
);

const AgencyInfoCard = ({ agency, canEditProfile = false, onAgencyUpdated }) => {
  const isActive = agency.status?.toLowerCase() === 'active' || agency.status === 'A';
  const territories = agency.territories || [];
  const hasPhysicalAddress = agency.physicalAddress?.street1;
  const hasMailingAddress = agency.mailingAddress?.street1;

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [states, setStates] = useState([]);

  const startEditing = async () => {
    // Fetch states on first edit
    if (states.length === 0) {
      const result = await getAllStates();
      if (result.success) setStates(result.data);
    }
    setFormData({
      email: agency.email || '',
      phone: formatPhoneNumber(agency.phone || ''),
      mobile: formatPhoneNumber(agency.mobile || ''),
      fax: formatPhoneNumber(agency.fax || ''),
      physicalAddress: { ...emptyAddress, ...agency.physicalAddress },
      mailingAddress: { ...emptyAddress, ...agency.mailingAddress },
    });
    setIsEditing(true);
  };

  const handlePhoneChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: formatPhoneNumber(value) }));
  };

  const handleCancel = () => setIsEditing(false);

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      ...formData,
      phone: stripPhoneFormatting(formData.phone),
      mobile: stripPhoneFormatting(formData.mobile),
      fax: stripPhoneFormatting(formData.fax),
    };
    const result = await updateAgencyProfile(agency.agencyId, payload);
    setSaving(false);

    if (result.success) {
      toast.success('Agency profile updated');
      setIsEditing(false);
      if (onAgencyUpdated) onAgencyUpdated(result.agency);
    } else {
      toast.error(result.error || 'Failed to update profile');
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 border-t-2 border-t-primary overflow-hidden">
      <div className="p-4">
        {/* Top row: names + status + edit button */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <BuildingOfficeIcon className="h-4.5 w-4.5 text-primary dark:text-primary-light" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                {agency.dbaAgencyName || agency.legalAgencyName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                #{agency.agencyId}
                {agency.legalAgencyName && agency.dbaAgencyName && agency.legalAgencyName !== agency.dbaAgencyName && (
                  <> &middot; {agency.legalAgencyName}</>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {agency.status && (
              <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded ${
                isActive
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}>
                {agency.status}
              </span>
            )}
            {canEditProfile && !isEditing && (
              <button
                onClick={startEditing}
                className="p-1.5 text-gray-400 hover:text-primary dark:hover:text-primary-light hover:bg-primary/10 rounded-lg transition-colors"
                title="Edit profile"
              >
                <PencilSquareIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {isEditing ? (
          /* ---- EDIT MODE ---- */
          <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-gray-700/50">
            {/* Contact fields */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <label className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">Email</label>
                <input type="email" autoComplete="off" data-1p-ignore data-lpignore="true" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={inputClass} placeholder="email@example.com" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">Phone</label>
                <input type="tel" autoComplete="off" data-1p-ignore data-lpignore="true" value={formData.phone} onChange={(e) => handlePhoneChange('phone', e.target.value)} className={inputClass} placeholder="(555) 555-5555" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">Mobile</label>
                <input type="tel" autoComplete="off" data-1p-ignore data-lpignore="true" value={formData.mobile} onChange={(e) => handlePhoneChange('mobile', e.target.value)} className={inputClass} placeholder="(555) 555-5555" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">Fax</label>
                <input type="tel" autoComplete="off" data-1p-ignore data-lpignore="true" value={formData.fax} onChange={(e) => handlePhoneChange('fax', e.target.value)} className={inputClass} placeholder="(555) 555-5555" />
              </div>
            </div>

            {/* Address fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <AddressForm label="Physical Address" address={formData.physicalAddress} onChange={(addr) => setFormData({ ...formData, physicalAddress: addr })} states={states} />
              <AddressForm
                label="Mailing Address"
                address={formData.mailingAddress}
                onChange={(addr) => setFormData({ ...formData, mailingAddress: addr })}
                states={states}
                actionButton={
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, mailingAddress: { ...formData.physicalAddress } })}
                    className="inline-flex items-center gap-1 text-[10px] text-primary dark:text-primary-light hover:text-primary-dark dark:hover:text-white transition-colors font-medium"
                  >
                    <DocumentDuplicateIcon className="h-3 w-3" />
                    Same as physical
                  </button>
                }
              />
            </div>

            {/* Save / Cancel */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700/50">
              <OathButton variant="outline" size="sm" onClick={handleCancel} disabled={saving}>
                <XMarkIcon className="h-3.5 w-3.5" />
                Cancel
              </OathButton>
              <OathButton size="sm" onClick={handleSave} loading={saving}>
                <CheckIcon className="h-3.5 w-3.5" />
                {saving ? 'Saving...' : 'Save'}
              </OathButton>
            </div>
          </div>
        ) : (
          /* ---- READ MODE ---- */
          <>
            {/* Contact row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700/50">
              <div className="min-w-0">
                <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Email</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <EnvelopeIcon className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                  <p className="text-sm text-gray-900 dark:text-white truncate">{agency.email || '-'}</p>
                </div>
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Phone</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <PhoneIcon className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
                  <p className="text-sm text-gray-900 dark:text-white truncate">{formatPhone(agency.phone) || '-'}</p>
                </div>
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Mobile</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <DevicePhoneMobileIcon className="h-3.5 w-3.5 text-violet-500 dark:text-violet-400 flex-shrink-0" />
                  <p className="text-sm text-gray-900 dark:text-white truncate">{formatPhone(agency.mobile) || '-'}</p>
                </div>
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Fax</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <PhoneIcon className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400 flex-shrink-0" />
                  <p className="text-sm text-gray-900 dark:text-white truncate">{formatPhone(agency.fax) || '-'}</p>
                </div>
              </div>
            </div>

            {/* Addresses row */}
            <div className={`grid gap-3 pt-3 mt-3 border-t border-gray-100 dark:border-gray-700/50 ${
              hasPhysicalAddress && hasMailingAddress ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'
            }`}>
              {hasPhysicalAddress && (
                <AddressBlock label="Physical Address" address={agency.physicalAddress} />
              )}
              {hasMailingAddress && (
                <AddressBlock label="Mailing Address" address={agency.mailingAddress} />
              )}
              {!hasPhysicalAddress && !hasMailingAddress && (
                <div>
                  <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Address</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-0.5">-</p>
                </div>
              )}
            </div>

            {/* Territories */}
            {territories.length > 0 && (
              <div className="pt-3 mt-3 border-t border-gray-100 dark:border-gray-700/50">
                <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
                  Territories ({territories.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {territories.map((t) => (
                    <span
                      key={t.countyId}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/20 text-xs text-indigo-700 dark:text-indigo-300 rounded border border-indigo-100 dark:border-indigo-800/40"
                    >
                      <GlobeAmericasIcon className="h-3 w-3 text-indigo-400 dark:text-indigo-400" />
                      {t.countyName}, {t.state}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const AgencyDetailsView = ({ agency, canEditProfile, onAgencyUpdated }) => {
  if (!agency) return null;
  return <AgencyInfoCard agency={agency} canEditProfile={canEditProfile} onAgencyUpdated={onAgencyUpdated} />;
};

// ============================================
// Tab definitions
// ============================================
const BASE_ADMIN_TABS = [
  { id: 'details', label: 'Details', icon: InformationCircleIcon },
  { id: 'users', label: 'Users', icon: UsersIcon, requiresPermission: 'users' },
  { id: 'payment-accounts', label: 'Payment Accounts', icon: BanknotesIcon },
  { id: 'executing-agents', label: 'Executing Agents', icon: IdentificationIcon },
];

// ============================================
// Main Admin Page
// ============================================
const AdminPage = () => {
  const { user: currentUser } = useOathAuth();
  const { canInAgency, isAdmin, canViewUsers } = usePermissions();

  const [searchParams, setSearchParams] = useSearchParams();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agencySearch, setAgencySearch] = useState('');
  const [executingAgentCount, setExecutingAgentCount] = useState(null);

  // Get agency ID and tab from URL
  const agencyIdFromUrl = searchParams.get('agencyId');
  const tabFromUrl = searchParams.get('tab') || 'details';

  // Derive selected agency from URL param and loaded agencies
  const selectedAgency = useMemo(() => {
    if (!agencyIdFromUrl || agencies.length === 0) return null;
    return agencies.find(a => String(a.agencyId) === agencyIdFromUrl) || null;
  }, [agencyIdFromUrl, agencies]);

  // Filter admin tabs based on permissions and add badge counts
  const adminTabs = useMemo(() => {
    return BASE_ADMIN_TABS
      .filter(tab => {
        if (tab.requiresPermission === 'users') return isAdmin || canViewUsers;
        return true;
      })
      .map(tab => {
        if (!selectedAgency) return tab;
        if (tab.id === 'users') return { ...tab, badge: selectedAgency.userCount ?? 0 };
        if (tab.id === 'payment-accounts') return { ...tab, badge: selectedAgency.paymentAccountCount ?? 0 };
        if (tab.id === 'executing-agents' && executingAgentCount !== null) return { ...tab, badge: executingAgentCount };
        return tab;
      });
  }, [isAdmin, canViewUsers, selectedAgency, executingAgentCount]);

  const activeTab = tabFromUrl;

  const fetchAgencies = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await getAgenciesForManagement();

    if (result.success) {
      const agencyList = result.agencies || [];
      setAgencies(agencyList);

      // If only one agency and no agency in URL, auto-select it
      if (agencyList.length === 1 && !agencyIdFromUrl) {
        setSearchParams({ agencyId: String(agencyList[0].agencyId) }, { replace: true });
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  }, [agencyIdFromUrl, setSearchParams]);

  useEffect(() => {
    fetchAgencies();
  }, [fetchAgencies]);

  // Fetch executing agent count when an agency is selected
  useEffect(() => {
    if (!selectedAgency) return;
    setExecutingAgentCount(null);
    const fetchCount = async () => {
      const result = await getExecutingAgents(selectedAgency.agencyId);
      if (result.success) {
        setExecutingAgentCount((result.agents || []).filter(a => a.statusCd === 'AC').length);
      }
    };
    fetchCount();
  }, [selectedAgency?.agencyId]);

  const handleSelectAgency = (agency) => {
    setSearchParams({ agencyId: String(agency.agencyId) });
  };

  const handleBackToAgencies = () => {
    setSearchParams({});
    setAgencySearch('');
  };

  const setActiveTab = (tab) => {
    setSearchParams({ agencyId: String(selectedAgency.agencyId), tab });
  };

  const getSubtitle = () => {
    if (selectedAgency) {
      return `Managing ${selectedAgency.dbaAgencyName || selectedAgency.legalAgencyName} (${selectedAgency.agencyId})`;
    }
    if (agencies.length > 1) {
      return 'Select an agency to manage';
    }
    return 'Manage users and permissions';
  };

  return (
    <OathPageWrapper maxWidth="max-w-7xl" responsive>
      {/* Page Header */}
      <div className="mb-6">
        <OathHeaderCard
          title="Agency Manager"
          subtitle={getSubtitle()}
          icon={BuildingOfficeIcon}
        />
      </div>

      {/* Content */}
      <OathContentCard>
        {selectedAgency ? (
          <div>
            {/* Back to agencies */}
            {agencies.length > 1 && (
              <button
                onClick={handleBackToAgencies}
                className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary-light transition-colors mb-4 group"
              >
                <ChevronLeftIcon className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
                <span>All Agencies</span>
                <span className="text-gray-300 dark:text-gray-600 mx-1">/</span>
                <span className="text-gray-700 dark:text-gray-200 font-medium">
                  {selectedAgency.dbaAgencyName || selectedAgency.legalAgencyName}
                </span>
              </button>
            )}

            {/* Tab navigation */}
            <AdminTabNavigation
              activeTab={activeTab}
              onTabChange={setActiveTab}
              tabs={adminTabs}
            />

            {/* Tab content */}
            {activeTab === 'details' && (
              <AgencyDetailsView
                key={selectedAgency.agencyId}
                agency={selectedAgency}
                canEditProfile={canInAgency(selectedAgency?.agencyId, 'UPDATE:AGENCY:PROFILE')}
                onAgencyUpdated={(updated) => {
                  setAgencies(prev => prev.map(a => a.agencyId === updated.agencyId ? { ...a, ...updated } : a));
                }}
              />
            )}
            {activeTab === 'users' && (
              <UsersListView
                key={selectedAgency.agencyId}
                agency={selectedAgency}
                currentUserId={currentUser?.portalUserId}
                canCreateUsers={canInAgency(selectedAgency?.agencyId, 'CREATE:USERS')}
                canEditUsers={isAdmin}
              />
            )}
            {activeTab === 'payment-accounts' && (
              <PaymentAccountsSection
                key={selectedAgency.agencyId}
                agency={selectedAgency}
                currentUserId={currentUser?.portalUserId}
                canManage={canInAgency(selectedAgency?.agencyId, 'UPDATE:AGENCY:PAYMENT-ACCOUNT')}
              />
            )}
            {activeTab === 'executing-agents' && (
              <ExecutingAgentsSection
                key={selectedAgency.agencyId}
                agency={selectedAgency}
                canEdit={canInAgency(selectedAgency?.agencyId, 'UPDATE:AGENCY:PROFILE')}
                currentProducerId={currentUser?.agencies?.find(a => a.agencyId === selectedAgency.agencyId)?.producerId}
                onCountLoaded={setExecutingAgentCount}
              />
            )}
          </div>
        ) : (
          <AgencySelectionView
            agencies={agencies}
            loading={loading}
            error={error}
            onRetry={fetchAgencies}
            onSelectAgency={handleSelectAgency}
            search={agencySearch}
            setSearch={setAgencySearch}
          />
        )}
      </OathContentCard>
    </OathPageWrapper>
  );
};

export default AdminPage;
